import { useEffect, useRef, useState } from "react";

export default function WhatsAppApproval({ protocolo, email, password }) {
  const [state, setState] = useState({ loading: true });
  const [retry, setRetry] = useState(0);
  const [importState, setImportState] = useState({});
  const sequence = useRef(0);
  useEffect(() => () => { sequence.current++; }, []);
  const request = async (action, values) => {
    const response = await fetch("/api/gestao", {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-gestao-email": email, "x-gestao-password": password },
      body: JSON.stringify({ action, protocolo, ...values }),
    });
    const body = await response.json();
    if (!response.ok || body.result !== "success") throw new Error(body.message || "Não foi possível conferir a notificação.");
    return body;
  };
  async function selectEmail(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const current = ++sequence.current;
    setImportState({ busy: true, name: file.name });
    try {
      if (!/\.eml$/i.test(file.name) || file.size <= 0 || file.size > 1024 * 1024) throw new Error("Selecione a notificação original .eml de até 1 MB.");
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(",")[1]);
        reader.onerror = () => reject(new Error("Não foi possível ler o arquivo."));
        reader.readAsDataURL(file);
      });
      const body = await request("previewApprovalEmailGestao", { emailBase64: base64 });
      if (current === sequence.current) setImportState({ base64, name: file.name, preview: body.preview, confirmed: false });
    } catch (error) {
      if (current === sequence.current) setImportState({ error: error.message });
    }
  }
  async function confirmImport() {
    if (!importState.confirmed || !importState.preview || importState.busy) return;
    const current = ++sequence.current;
    setImportState(previous => ({ ...previous, busy: true, error: "" }));
    try {
      const body = await request("importApprovalEmailGestao", { emailBase64: importState.base64, previewKey: importState.preview.previewKey, confirmed: true });
      if (current === sequence.current) {
        setImportState({ message: body.message });
        setRetry(value => value + 1);
      }
    } catch (error) {
      if (current === sequence.current) setImportState(previous => ({ ...previous, busy: false, confirmed: false, error: error.message }));
    }
  }
  useEffect(() => {
    const controller = new AbortController();
    setState({ loading: true });
    if (!protocolo) {
      setState({ message: "Protocolo não informado. Compartilhamento indisponível." });
      return;
    }
    fetch("/api/gestao", {
      method: "POST", signal: controller.signal,
      headers: { "Content-Type": "application/json", "x-gestao-email": email, "x-gestao-password": password },
      body: JSON.stringify({ action: "whatsappGestao", protocolo }),
    }).then(async response => {
      const body = await response.json();
      if (!response.ok || body.result !== "success") throw new Error(body.message || "Compartilhamento indisponível.");
      setState(body);
    }).catch(error => { if (error.name !== "AbortError") setState({ message: error.message }); });
    return () => controller.abort();
  }, [protocolo, email, password, retry]);
  let safeUrl = "";
  try {
    const url = new URL(state.url);
    if (state.available && url.origin === "https://wa.me" && /^\/55\d{11}$/.test(url.pathname) && !url.username && !url.password) safeUrl = url.href;
  } catch { /* A missing link must not fall back to the PDF. */ }
  return <section aria-label="Compartilhamento da aprovação" style={{ borderTop: "1px solid #d8e6ef", borderBottom: "1px solid #d8e6ef", padding: "14px 0" }}>
    <strong>Aprovação pelo WhatsApp</strong>
    {importState.message && <p role="status" style={{ fontSize: 13 }}>{importState.message}</p>}
    {state.loading ? <p role="status">Conferindo etapa e link da aprovação...</p> : <>
      {safeUrl ? <>
        <p style={{ fontSize: 13 }}>{state.stage}: {state.email}</p>
        {state.approvalLink && <p style={{ margin: "8px 0" }}><a href={state.approvalLink} target="_blank" rel="noopener noreferrer">Abrir aprovação pendente</a></p>}
        <a href={safeUrl} target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", padding: "10px 14px", borderRadius: 6, background: "#176541", color: "#fff", fontWeight: 700, textDecoration: "none" }}>Enviar aprovação pelo WhatsApp</a>
        <p style={{ fontSize: 12 }}>Confirme o envio no WhatsApp. A decisão permanece na mesma aprovação Microsoft.</p>
      </> : <p role="status" style={{ fontSize: 13 }}>{state.message || "Link de aprovação indisponível."}</p>}
      {state.canImport && <div style={{ marginTop: 12, display: "grid", gap: 10, fontSize: 13 }}>
        <label style={{ display: "grid", gap: 6 }}>
          <span>Notificação original (.eml)</span>
          <input type="file" accept=".eml,message/rfc822" onChange={selectEmail} disabled={importState.busy} style={{ width: "100%", minHeight: 40, font: "inherit" }} />
        </label>
        {importState.name && <span style={{ overflowWrap: "anywhere" }}>Arquivo em conferência: {importState.name}</span>}
        {importState.busy && <p role="status">Conferindo a notificação...</p>}
        {importState.preview && <>
          <dl style={{ display: "grid", gridTemplateColumns: "90px minmax(0,1fr)", gap: "6px 10px", margin: 0, overflowWrap: "anywhere" }}>
            <dt>Protocolo</dt><dd style={{ margin: 0 }}>{importState.preview.record.protocolo}</dd>
            <dt>Etapa</dt><dd style={{ margin: 0 }}>{importState.preview.stageLabel}</dd>
            <dt>Aprovador</dt><dd style={{ margin: 0 }}>{importState.preview.record.emailAprovador}</dd>
            <dt>Conferência</dt><dd style={{ margin: 0 }}>{importState.preview.association}</dd>
          </dl>
          <a href={importState.preview.record.approvalLink} target="_blank" rel="noopener noreferrer">Abrir aprovação para conferir</a>
          <p style={{ margin: 0 }}>A leitura do arquivo não consulta o status no Microsoft. Nenhuma decisão será registrada por esta importação.</p>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, lineHeight: 1.5 }}>
            <input type="checkbox" checked={Boolean(importState.confirmed)} disabled={importState.busy} onChange={event => setImportState(previous => ({ ...previous, confirmed: event.target.checked }))} style={{ marginTop: 3, flexShrink: 0 }} />
            <span>Conferi no e-mail ou Teams que esta aprovação está pendente e pertence ao protocolo, à etapa e ao aprovador acima.</span>
          </label>
          <button type="button" disabled={!importState.confirmed || importState.busy} onClick={confirmImport} style={{ minHeight: 44, padding: "10px 12px", borderRadius: 6, border: "1px solid #0d5f8f", background: importState.confirmed ? "#0d5f8f" : "#edf2f6", color: importState.confirmed ? "#fff" : "#52697b", cursor: importState.confirmed ? "pointer" : "default", fontWeight: 700 }}>Registrar link da aprovação</button>
        </>}
        {importState.error && <p role="alert" style={{ color: "#9a3025", margin: 0 }}>{importState.error}</p>}
      </div>}
      <button type="button" onClick={() => setRetry(v => v + 1)} style={{ marginTop: 8, minHeight: 40, padding: "8px 12px", background: "#fff", border: "1px solid #bfd3e4", borderRadius: 6, color: "#0d5f8f", cursor: "pointer" }}>Atualizar aprovação</button>
    </>}
  </section>;
}
